import { useEffect, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import useGameStore from '../stores/gameStore';
import useUIStore from '../stores/uiStore';
import useAuthStore from '../stores/authStore';
import apiService from '../services/api';
import guestService from '../services/guestService';
import { config } from '../config';

interface WebSocketState {
  socket: Socket | null;
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  reconnectAttempts: number;
}

interface WebSocketActions {
  connect: (sessionId: string, playerId: string) => void;
  disconnect: () => void;
  joinSession: (sessionId: string, playerId: string) => void;
  leaveSession: (sessionId: string, playerId: string) => void;
  sendPlayerAction: (sessionId: string, playerId: string, optionId: string) => void;
  sendPlayerReady: (sessionId: string, playerId: string) => void;
}

export const useWebSocket = (): WebSocketState & WebSocketActions => {
  const gameStore = useGameStore();
  const uiStore = useUIStore();
  const socketRef = useRef<Socket | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const gameStoreRef = useRef(gameStore);
  const isConnectingRef = useRef(false);
  const autoTransitionInProgressRef = useRef(false);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 1000; // Start with 1 second

  // Keep the gameStore ref up to date
  gameStoreRef.current = gameStore;

  const connect = useCallback((sessionId: string, playerId: string) => {
    if (socketRef.current?.connected) {
      return; // Already connected
    }

    if (isConnectingRef.current) {
      return; // Already connecting
    }

    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    isConnectingRef.current = true;

    try {
      // Get current authentication token (guest or regular)
      const authState = useAuthStore.getState();
      let token: string | null = null;
      
      if (authState.isGuest) {
        // For guest users, use guest token from guestService or auth store
        const guestData = guestService.getGuestData();
        token = guestData?.token || authState.token;
      } else if (authState.isAuthenticated && authState.token) {
        // For authenticated users, use regular token from auth store
        token = authState.token;
      }
      
      // Create socket with authentication token in handshake
      const socket = io(config.websocketUrl, {
        transports: ['websocket', 'polling'],
        timeout: 10000,
        forceNew: false, // Don't force new connection
        ...(token && { auth: { token } }), // Send token in handshake.auth.token
        ...(token && { extraHeaders: { Authorization: `Bearer ${token}` } }), // Also send in Authorization header as fallback
      });

      socketRef.current = socket;

      // Connection events
      socket.on('connect', async () => {
        isConnectingRef.current = false;
        gameStore.setConnectionStatus(true);
        console.log(`[useWebSocket] DEBUG: Socket connected: ${socket.id}, sessionId: ${sessionId}, playerId: ${playerId}`);
        console.log(`[useWebSocket] DEBUG: Socket auth token present: ${!!token}`);
        console.log(`[useWebSocket] DEBUG: Auth state - isGuest: ${authState.isGuest}, isAuthenticated: ${authState.isAuthenticated}`);
        
        // Join the session room
        console.log(`[useWebSocket] DEBUG: Emitting join-session event...`);
        joinSession(sessionId, playerId);
        
        // After connecting, check for pending friend requests and reload scene if needed
        // This handles the case where a friend request was sent while the websocket was disconnected
        // We check immediately and also after a delay to catch any that arrive in between
        const checkPendingFriendRequests = async () => {
          if (!gameStoreRef.current.currentSession || !gameStoreRef.current.currentPlayer) {
            console.warn('[useWebSocket] Cannot check for pending friend requests - no current session or player');
            return;
          }
          
          // Skip friend request check for guest users - they don't have friends
          const { default: useAuthStore } = await import('../stores/authStore');
          const authState = useAuthStore.getState();
          if (authState.isGuest) {
            console.log('[useWebSocket] DEBUG: Skipping friend request check - user is a guest');
            return;
          }
          
          try {
            console.log('[useWebSocket] DEBUG: Checking for pending friend requests...');
            const friendsService = (await import('../services/friendsService')).default;
            const pendingRequests = await friendsService.getFriendRequests();
            console.log(`[useWebSocket] DEBUG: getFriendRequests() returned ${pendingRequests.length} request(s):`, pendingRequests);
            if (pendingRequests.length > 0) {
              console.log(`[useWebSocket] DEBUG: Found ${pendingRequests.length} pending friend request(s), reloading scene...`);
              await gameStoreRef.current.loadCurrentScene();
              console.log('[useWebSocket] DEBUG: Scene reloaded after finding pending friend requests');
            } else {
              console.log('[useWebSocket] DEBUG: No pending friend requests found');
            }
          } catch (error: any) {
            // Ignore 401 errors (unauthorized) - this is expected when the user is not fully authenticated yet
            if (error?.response?.status === 401 || error?.status === 401) {
              console.log('[useWebSocket] DEBUG: Skipping friend request check - user not authenticated');
            } else {
              console.error('[useWebSocket] DEBUG: Failed to check for pending friend requests:', error);
            }
          }
        };
        
        // Check immediately (in case friend request was sent before socket connected)
        checkPendingFriendRequests();
        
        // Also check after a delay to catch any that arrive while the socket is connecting
        setTimeout(checkPendingFriendRequests, 3000);
      });

      socket.on('disconnect', (reason) => {
        gameStore.setConnectionStatus(false);
        
        if (reason === 'io server disconnect') {
          // Server initiated disconnect, try to reconnect
          attemptReconnect(sessionId, playerId);
        }
      });

      socket.on('connect_error', (error) => {
        console.error('WebSocket connection error:', error);
        isConnectingRef.current = false;
        uiStore.setError({ code: 'WEBSOCKET_ERROR', message: 'Failed to connect to game server' });
        attemptReconnect(sessionId, playerId);
      });

      // Game events
      socket.on('join-confirmed', (data) => {
        console.log('[useWebSocket] DEBUG: join-confirmed event received:', data);
        console.log('[useWebSocket] DEBUG: Current session:', gameStoreRef.current.currentSession?.id);
        console.log('[useWebSocket] DEBUG: Current player:', gameStoreRef.current.currentPlayer?.id);
        // Session join confirmed - no toast needed
      });

      socket.on('leave-confirmed', (_data) => {
        uiStore.showToast('Left game session', 'info');
      });

      socket.on('state-update', async (data) => {
        gameStore.updateGameState(data);
        
        // Handle different types of state updates
        if (data.type === 'session_started') {
          // Session has started, reload session data and redirect to scene
          const { currentSession, currentPlayer } = gameStoreRef.current;
          
          if (currentSession && currentPlayer) {
            try {
              // Update session status
              gameStoreRef.current.setLoading(true);
              
              // Reload session players to get updated state
              await gameStoreRef.current.loadSessionPlayers(currentSession.id);
              
              // Navigate to the player's starting scene
              window.location.href = `/scene/${currentSession.id}/${currentPlayer.id}`;
            } catch (error) {
              console.error('Failed to handle session start:', error);
            }
          }
        } else if (data.type === 'player_joined' || data.type === 'player_left') {
          // Reload session players when someone joins or leaves
          const { currentSession } = gameStoreRef.current;
          if (currentSession) {
            try {
              await gameStoreRef.current.loadSessionPlayers(currentSession.id);
            } catch (error) {
              console.error('Failed to reload session players:', error);
            }
          }
        } else if (data.type === 'player_action' && data.gameState) {
          // If this is a player action update, fetch the updated scene
          const { currentSession, currentPlayer } = gameStoreRef.current;
          if (currentSession && currentPlayer) {
            try {
              await gameStoreRef.current.loadCurrentScene();
            } catch (error) {
              console.error('Failed to fetch updated scene:', error);
            }
          }
        }
      });

      // Adventure invite events
      socket.on('adventure_invite_received', async () => {
        // Trigger UI update for adventure invites list
        window.dispatchEvent(new CustomEvent('adventure_invite:update'));
        
        // Reload scene to update conditional sections (like getAdventureInviteCount())
        const { currentSession, currentPlayer } = gameStoreRef.current;
        if (currentSession && currentPlayer) {
          try {
            await gameStoreRef.current.loadCurrentScene();
          } catch (error) {
            console.error('Failed to reload scene after adventure invite received:', error);
          }
        }
      });

      socket.on('adventure_invite_accepted', () => {
        // Trigger UI update for sent invites and session state
        window.dispatchEvent(new CustomEvent('adventure_invite:update'));
      });

      socket.on('adventure_invite_rejected', () => {
        // Trigger UI update for sent invites list
        window.dispatchEvent(new CustomEvent('adventure_invite:update'));
      });

      socket.on('adventure_invite_cancelled', () => {
        // Trigger UI update for received invites list
        window.dispatchEvent(new CustomEvent('adventure_invite:update'));
      });

      socket.on('waiting-update', async (data) => {
        // Handle authentication tokens if provided (e.g., from async login completion)
        let tokensUpdated = false;
        if (data && data.token && data.refreshToken && data.userId) {
          console.log(`[useWebSocket] Received tokens in waiting-update event for user ${data.userId}`);
          try {
            const { default: useAuthStore } = await import('../stores/authStore');
            const authStore = useAuthStore.getState();
            const authService = (await import('../services/auth')).default;
            
            // Update tokens using authService FIRST (highest priority for axios interceptor)
            // The interceptor now checks authService.getCurrentToken() first, so this is critical
            // This is synchronous and eliminates race conditions with Zustand state updates
            (authService as any).setTokens(data.token, data.refreshToken);
            
            // Update auth store to sync user state (for UI and fallback)
            // IMPORTANT: After login, user is no longer a guest, so we need to clear guest state
            authStore.setToken(data.token);
            
            // Clear guest state since user is now authenticated
            // Note: This is now less critical since interceptor checks authService first,
            // but we still do it for consistency and fallback scenarios
            authStore.clearGuestData();
            
            // Try to get user info, but don't fail if it doesn't work
            try {
              const user = await authService.getCurrentUser();
              authStore.setUser(user);
            } catch (userError) {
              // If getCurrentUser fails, we can still set the token
              // The user will be loaded on next API call
              console.warn('[useWebSocket] Could not get user info, but tokens are set:', userError);
            }
            
            tokensUpdated = true;
            console.log(`[useWebSocket] Tokens updated successfully from waiting-update event`);
            
            // Verify tokens are actually set in authService
            const verifyAuthService = (await import('../services/auth')).default;
            const hasToken = !!(verifyAuthService as any).jwtToken;
            console.log(`[useWebSocket] Token verification: hasToken=${hasToken}`);
            
            if (!hasToken) {
              console.error('[useWebSocket] WARNING: Tokens were not set in authService!');
            }
            
            // Wait longer to ensure tokens are fully propagated to axios interceptor
            // The axios interceptor reads this.jwtToken, which is set by setTokens
            // Also wait for Zustand state to update (clearGuestData updates isGuest)
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Double-check token is still available after delay
            const verifyAuthServiceAgain = (await import('../services/auth')).default;
            const hasTokenAfterDelay = !!(verifyAuthServiceAgain as any).jwtToken;
            console.log(`[useWebSocket] Token verification after delay: hasToken=${hasTokenAfterDelay}`);
          } catch (tokenError) {
            console.error('[useWebSocket] Failed to update tokens from waiting-update event:', tokenError);
          }
        }
        
        // Refresh the current scene to get updated waiting options
        // This triggers condition re-evaluation, which will enable waiting options
        // when their conditions are met (e.g., async operations complete)
        const { currentSession, currentPlayer } = gameStoreRef.current;
        if (currentSession && currentPlayer) {
          // Optionally filter by playerId if provided in event data
          // This ensures we only reload for the relevant player
          if (data && data.playerId && data.playerId !== currentPlayer.id) {
            return; // Event is for a different player
          }

          try {
            // If tokens were just updated, verify they're available before reloading scene
            if (tokensUpdated) {
              const verifyAuthService = (await import('../services/auth')).default;
              const hasToken = !!(verifyAuthService as any).jwtToken;
              if (!hasToken) {
                console.error('[useWebSocket] WARNING: Attempting to reload scene but tokens are not available!');
                // Wait a bit more and try again
                await new Promise(resolve => setTimeout(resolve, 300));
                const verifyAuthServiceAgain = (await import('../services/auth')).default;
                const hasTokenAfterRetry = !!(verifyAuthServiceAgain as any).jwtToken;
                if (!hasTokenAfterRetry) {
                  console.error('[useWebSocket] ERROR: Tokens still not available after retry, skipping scene reload');
                  return;
                }
              }
            }
            
            // Track previous waiting options before reloading
            const previousWaitingOptionIds = Array.isArray(gameStoreRef.current.waitingOptionIds)
              ? [...gameStoreRef.current.waitingOptionIds]
              : [];
            const previousAvailableOptionIds = Array.isArray(gameStoreRef.current.availableOptionIds)
              ? [...gameStoreRef.current.availableOptionIds]
              : [];
            
            console.log(`[useWebSocket] Before scene reload - waiting: ${previousWaitingOptionIds.length}, available: ${previousAvailableOptionIds.length}`);
            
            // Reload scene to trigger condition re-evaluation
            // This will update availableOptionIds and waitingOptionIds based on current conditions
            await gameStoreRef.current.loadCurrentScene();
            
            // Detect auto-transition opportunities after scene reload
            const currentState = gameStoreRef.current;
            const currentAvailableOptionIds = currentState.availableOptionIds || [];
            const currentWaitingOptionIds = currentState.waitingOptionIds || [];
            
            console.log(`[useWebSocket] After scene reload - waiting: ${currentWaitingOptionIds.length}, available: ${currentAvailableOptionIds.length}`);
            
            // Check for auto-transition in two scenarios:
            // 1. Options that moved from waiting to available (newly available)
            // 2. Options that are currently available with autoTransition (in case condition was already met)
            const newlyAvailableOptionIds = currentAvailableOptionIds.filter(
              optionId => previousWaitingOptionIds.includes(optionId)
            );
            
            console.log(`[useWebSocket] Newly available options: ${newlyAvailableOptionIds.length}`);
            
            // Find auto-transition options: first check newly available, then check all available options
            // This handles cases where the condition was already met when waiting-update arrived
            let autoTransitionOptionId: string | undefined;
            
            if (newlyAvailableOptionIds.length > 0) {
              // First priority: options that just became available
              autoTransitionOptionId = newlyAvailableOptionIds.find(optionId => {
                const option = currentState.optionsMap[optionId];
                const hasAutoTransition = option?.autoTransition === true;
                console.log(`[useWebSocket] Checking newly available option ${optionId}: autoTransition=${hasAutoTransition}`);
                return hasAutoTransition;
              });
            }
            
            // Second priority: check all currently available options with autoTransition
            // This handles edge cases where condition was already true when waiting-update arrived
            // OR where the option was already available but auto-transition didn't fire yet
            if (!autoTransitionOptionId && currentAvailableOptionIds.length > 0) {
              console.log(`[useWebSocket] Checking all available options for auto-transition (${currentAvailableOptionIds.length} options)`);
              for (const optionId of currentAvailableOptionIds) {
                const option = currentState.optionsMap[optionId];
                console.log(`[useWebSocket] Checking option ${optionId}: text="${option?.text}", autoTransition=${option?.autoTransition}, hasWaitMessage=${!!option?.waitMessage}, hasCondition=${!!option?.condition}`);
                if (option?.autoTransition === true) {
                  console.log(`[useWebSocket] Found auto-transition option ${optionId} in available options`);
                  autoTransitionOptionId = optionId;
                  break;
                }
              }
            }
              
            if (autoTransitionOptionId) {
              console.log(`[useWebSocket] Auto-transition detected for option ${autoTransitionOptionId} (was waiting: ${previousWaitingOptionIds.includes(autoTransitionOptionId)})`);
              // Prevent concurrent transitions
              if (autoTransitionInProgressRef.current) {
                return; // Already processing an auto-transition
              }
              
              try {
                autoTransitionInProgressRef.current = true;
                
                // Re-validate condition: check if option is still in availableOptionIds
                // (This prevents race conditions where condition becomes false between detection and transition)
                const currentAvailableOptionIds = gameStoreRef.current.availableOptionIds;
                if (!currentAvailableOptionIds.includes(autoTransitionOptionId)) {
                  // Condition is no longer true, skip auto-transition
                  autoTransitionInProgressRef.current = false;
                  return;
                }
                
                // Flush any pending user inputs before transition
                if (typeof gameStoreRef.current.flushPendingUserInputs === 'function') {
                  await gameStoreRef.current.flushPendingUserInputs();
                }
                
                // Re-check condition one more time after flushing inputs
                const finalAvailableOptionIds = gameStoreRef.current.availableOptionIds;
                if (!finalAvailableOptionIds.includes(autoTransitionOptionId)) {
                  // Condition changed during input flush, skip auto-transition
                  autoTransitionInProgressRef.current = false;
                  return;
                }
                
                // Execute auto-transition using REST API directly (not via useGameState.selectOption)
                // This ensures updated tokens are used via axios interceptor
                // useGameState.selectOption might use WebSocket which has old tokens
                // If tokens were just updated, wait a bit more to ensure they're in the axios interceptor
                if (tokensUpdated) {
                  console.log(`[useWebSocket] Waiting additional time for tokens to propagate before auto-transition`);
                  await new Promise(resolve => setTimeout(resolve, 300));
                }
                
                // Use gameStore.selectOption directly (REST API) instead of useGameState.selectOption (might use WebSocket)
                console.log(`[useWebSocket] Executing auto-transition for option ${autoTransitionOptionId} via REST API`);
                await gameStoreRef.current.selectOption(autoTransitionOptionId);
              } catch (error) {
                // Log error but don't crash - option remains available for manual selection
                console.error('Failed to execute auto-transition:', error);
              } finally {
                autoTransitionInProgressRef.current = false;
              }
            }
          } catch (error) {
            console.error('Failed to refresh scene after waiting update:', error);
            autoTransitionInProgressRef.current = false;
          }
        }
      });

      socket.on('item-change', async (data) => {
        // Debug: log item-change payload
        
        
        // Handle item change notifications with toast
        if (data.itemChanges && Array.isArray(data.itemChanges)) {
          const { currentSession, currentPlayer } = gameStoreRef.current;
          
          if (currentSession) {
            // Aggregate changes by itemTag
            const changesByTag = new Map<string, Set<'added' | 'removed'>>();
            for (const change of data.itemChanges) {
              if (!changesByTag.has(change.itemTag)) changesByTag.set(change.itemTag, new Set());
              changesByTag.get(change.itemTag)!.add(change.changeType);
            }

            // Dispatch UI refresh for current player inventory once per tag
            if (currentPlayer) {
              for (const [itemTag, changeSet] of changesByTag.entries()) {
                const effectiveChange: 'added' | 'removed' = changeSet.has('added') ? 'added' : 'removed';
                try {
                  window.dispatchEvent(new CustomEvent('inventory:update', {
                    detail: {
                      ownerId: currentPlayer.id,
                      ownerType: 'character',
                      itemTag,
                      changeType: effectiveChange
                    }
                  }));
                } catch (e) {
                  void e;
                }
              }
            }

            // Only show toasts for current player's inventory, and prefer 'added' if both present
            if (currentPlayer) {
              for (const [itemTag, changeSet] of changesByTag.entries()) {
                const effectiveChange: 'added' | 'removed' = changeSet.has('added') ? 'added' : 'removed';
                try {
                  const itemData = await apiService.getItemData(currentSession.id, itemTag);
                  if (effectiveChange === 'added') {
                    uiStore.showToast(`Added item "${itemData.name}"`, 'success', 5000);
                  } else {
                    uiStore.showToast(`Removed item "${itemData.name}"`, 'info', 5000);
                  }
                } catch (error) {
                  console.error('Failed to fetch item data for notification:', error);
                  if (effectiveChange === 'added') {
                    uiStore.showToast(`Added item "${itemTag}"`, 'success', 5000);
                  } else {
                    uiStore.showToast(`Removed item "${itemTag}"`, 'info', 5000);
                  }
                }
              }
            }
          }
        }
      });

      // Handle unified inventory events
      socket.on('inventory-update', async (data) => {
        // Debug: log inventory-update payload
        
        // Avoid unused destructuring; toasts handled in item-change. We still need gameStoreRef for dispatch context.
        
        // Always notify UI components first so lists can refetch regardless of itemTag presence
        if (data && data.ownerId && data.ownerType) {
          try {
            window.dispatchEvent(new CustomEvent('inventory:update', {
              detail: {
                ownerId: data.ownerId,
                ownerType: data.ownerType,
                itemTag: data.itemTag,
                changeType: data.changeType
              }
            }));
          } catch (e) {
            void e;
          }
        }

        // Also surface a toast for unified events when an itemTag is present
        // This ensures tests and UIs that rely on unified events still get user feedback
        try {
          const { currentSession } = gameStoreRef.current;
          if (currentSession && data && data.itemTag) {
            const itemData = await apiService.getItemData(currentSession.id, data.itemTag);
            const changeType: 'added' | 'removed' | undefined = data.changeType;
            let targetLabel = '';
            if (data.ownerType === 'character') {
              const player = currentSession.players?.find(p => p.id === data.ownerId);
              targetLabel = player?.characterName || 'Character';
            } else if (data.ownerType === 'location') {
              targetLabel = data.ownerId || 'location';
            }

            const verb = changeType === 'removed' ? 'Removed' : 'Added';
            const level = changeType === 'removed' ? 'info' : 'success';
            uiStore.showToast(`${verb} "${itemData.name}" ${data.ownerType === 'character' ? `to ${targetLabel}` : `from ${targetLabel}`}`, level as any, 5000);
          }
        } catch (error) {
          // Fall back to itemTag if item data lookup fails
          const { currentSession } = gameStoreRef.current;
          if (currentSession && data && data.itemTag) {
            const changeType: 'added' | 'removed' | undefined = data.changeType;
            let targetLabel = '';
            if (data.ownerType === 'character') {
              const player = currentSession.players?.find(p => p.id === data.ownerId);
              targetLabel = player?.characterName || 'Character';
            } else if (data.ownerType === 'location') {
              targetLabel = data.ownerId || 'location';
            }
            const verb = changeType === 'removed' ? 'Removed' : 'Added';
            const level = changeType === 'removed' ? 'info' : 'success';
            uiStore.showToast(`${verb} "${data.itemTag}" ${data.ownerType === 'character' ? `to ${targetLabel}` : `from ${targetLabel}`}`, level as any, 5000);
          }
        }
      });

      socket.on('user-input-update', (data) => {
        if (!data || !data.variableName) {
          return;
        }

        gameStoreRef.current.setUserVariableValue(
          data.variableName,
          data.value ?? '',
          data.scope
        );
      });

      // Handle friend request events
      socket.on('friend_request_received', async (data) => {
        console.log('[useWebSocket] DEBUG: friend_request_received event received:', data);
        console.log('[useWebSocket] DEBUG: Current session:', gameStoreRef.current.currentSession?.id);
        console.log('[useWebSocket] DEBUG: Current player:', gameStoreRef.current.currentPlayer?.id);
        console.log('[useWebSocket] DEBUG: Socket connected:', socketRef.current?.connected);
        console.log('[useWebSocket] DEBUG: Socket id:', socketRef.current?.id);
        
        // Notify UI components to refresh friend requests
        window.dispatchEvent(new Event('friend_request:update'));
        
        // Refresh the current scene to re-evaluate conditionals (e.g., getPendingRequestCount() > 0)
        if (gameStoreRef.current.currentSession && gameStoreRef.current.currentPlayer) {
          console.log('[useWebSocket] DEBUG: Reloading current scene after friend request received');
          console.log('[useWebSocket] DEBUG: Before reload - scene ID:', gameStoreRef.current.currentScene?.id);
          try {
            await gameStoreRef.current.loadCurrentScene();
            console.log('[useWebSocket] DEBUG: Scene reloaded successfully');
            console.log('[useWebSocket] DEBUG: After reload - scene ID:', gameStoreRef.current.currentScene?.id);
            console.log('[useWebSocket] DEBUG: After reload - scene sections count:', gameStoreRef.current.currentScene?.sections?.length);
            
            // Check if friend request text is in the scene
            const sceneText = JSON.stringify(gameStoreRef.current.currentScene);
            const hasFriendRequestText = sceneText?.toLowerCase().includes('pending') || sceneText?.toLowerCase().includes('friend request');
            console.log('[useWebSocket] DEBUG: Scene contains friend request text:', hasFriendRequestText);
          } catch (error) {
            console.error('[useWebSocket] DEBUG: Failed to reload scene:', error);
          }
        } else {
          console.warn('[useWebSocket] DEBUG: Cannot reload scene - no current session or player');
        }
        
        if (data && data.request) {
          uiStore.showToast('You received a new friend request', 'info', 5000);
        }
      });

      socket.on('friend_request_accepted', (data) => {
        // Notify UI components to refresh friends and sent requests
        window.dispatchEvent(new Event('friend_request:update'));
        window.dispatchEvent(new Event('friend:update'));
        if (data && data.request) {
          uiStore.showToast('Your friend request was accepted', 'success', 5000);
        }
      });

      socket.on('friend_request_rejected', (data) => {
        // Notify UI components to refresh sent requests
        window.dispatchEvent(new Event('friend_request:update'));
        if (data && data.request) {
          uiStore.showToast('Your friend request was rejected', 'info', 5000);
        }
      });

      socket.on('friend_added', (data) => {
        // Notify UI components to refresh friends list
        window.dispatchEvent(new Event('friend:update'));
        if (data && data.friend) {
          uiStore.showToast('You are now friends', 'success', 5000);
        }
      });

      socket.on('friend_removed', (data) => {
        // Notify UI components to refresh friends list
        window.dispatchEvent(new Event('friend:update'));
        if (data && data.friend) {
          uiStore.showToast('Friend removed', 'info', 5000);
        }
      });

      socket.on('error', (error) => {
        console.error('WebSocket error:', error);
        uiStore.setError({ code: 'WEBSOCKET_ERROR', message: error.message });
      });

    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      isConnectingRef.current = false;
      uiStore.setError({ code: 'WEBSOCKET_ERROR', message: 'Failed to create connection' });
    }
  }, [gameStore, uiStore]);

  const disconnect = useCallback(() => {
    isConnectingRef.current = false;
    
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    
    if (reconnectTimeoutRef.current) {
      window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    gameStoreRef.current.setConnectionStatus(false);
  }, []); // No dependencies to prevent infinite loops

  const joinSession = useCallback((sessionId: string, playerId: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('join-session', { sessionId, playerId });
    }
  }, []);

  const leaveSession = useCallback((sessionId: string, playerId: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('leave-session', { sessionId, playerId });
    }
  }, []);

  const sendPlayerAction = useCallback((sessionId: string, playerId: string, optionId: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('player-action', { sessionId, playerId, optionId });
    } else {
      uiStore.setError({ code: 'WEBSOCKET_ERROR', message: 'Not connected to game server' });
    }
  }, [uiStore]);

  const sendPlayerReady = useCallback((sessionId: string, playerId: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('player-ready', { sessionId, playerId });
    }
  }, []);

  const attemptReconnect = useCallback((sessionId: string, playerId: string) => {
    if (gameStoreRef.current.isConnected) {
      return; // Already connected
    }

    const currentAttempts = gameStoreRef.current.reconnectAttempts;
    
    if (currentAttempts >= maxReconnectAttempts) {
      uiStore.setError({ 
        code: 'WEBSOCKET_ERROR', 
        message: 'Failed to reconnect after multiple attempts' 
      });
      return;
    }

    const delay = reconnectDelay * Math.pow(2, currentAttempts); // Exponential backoff
    
    // Update reconnect attempts
    gameStoreRef.current.setReconnectAttempts(currentAttempts + 1);
    
    reconnectTimeoutRef.current = window.setTimeout(() => {
      connect(sessionId, playerId);
    }, delay);
  }, [uiStore, connect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  // Track previous session/player IDs to detect changes
  const prevSessionIdRef = useRef<string | undefined>(undefined);
  const prevPlayerIdRef = useRef<string | undefined>(undefined);

  // Auto-connect when session and player are available
  useEffect(() => {
    const { currentSession, currentPlayer } = gameStoreRef.current;
    
    if (!currentSession || !currentPlayer) {
      return;
    }
    
    const sessionId = currentSession.id;
    const playerId = currentPlayer.id;
    
    // Check if session or player has changed
    const sessionChanged = prevSessionIdRef.current !== undefined && prevSessionIdRef.current !== sessionId;
    const playerChanged = prevPlayerIdRef.current !== undefined && prevPlayerIdRef.current !== playerId;
    
    // If session or player changed and we're connected, disconnect first
    if ((sessionChanged || playerChanged) && socketRef.current?.connected) {
      // Leave the old session before disconnecting
      if (prevSessionIdRef.current && prevPlayerIdRef.current) {
        leaveSession(prevSessionIdRef.current, prevPlayerIdRef.current);
      }
      disconnect();
    }
    
    // Update refs
    prevSessionIdRef.current = sessionId;
    prevPlayerIdRef.current = playerId;
    
    // Connect if not connected and not already connecting
    if (!gameStoreRef.current.isConnected && !isConnectingRef.current) {
      connect(sessionId, playerId);
    }
  }, [gameStore.currentSession?.id, gameStore.currentPlayer?.id, gameStore.isConnected, connect, disconnect, leaveSession]);

  // Watch for auth state changes (guest -> authenticated) and reconnect websocket
  const isGuest = useAuthStore((state) => state.isGuest);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const wasGuestRef = useRef<boolean | null>(null);
  
  useEffect(() => {
    // Initialize ref on first render
    if (wasGuestRef.current === null) {
      wasGuestRef.current = isGuest;
      return;
    }
    
    // Check if user transitioned from guest to authenticated
    if (wasGuestRef.current === true && isGuest === false && isAuthenticated) {
      // User was a guest and is now authenticated - reconnect websocket with new token
      const { currentSession, currentPlayer } = gameStoreRef.current;
      console.log(`[useWebSocket] Guest user became authenticated! Current session: ${currentSession?.id}, player: ${currentPlayer?.id}, socket connected: ${socketRef.current?.connected}`);
      
      if (currentSession && currentPlayer && socketRef.current?.connected) {
        console.log('[useWebSocket] Guest user became authenticated, reconnecting websocket with new token...');
        // Disconnect and reconnect with new token
        disconnect();
        // Small delay to ensure disconnect completes
        setTimeout(() => {
          const { currentSession: session, currentPlayer: player } = gameStoreRef.current;
          if (session && player) {
            console.log(`[useWebSocket] Reconnecting websocket for session ${session.id}, player ${player.id}`);
            connect(session.id, player.id);
          } else {
            console.warn('[useWebSocket] Session or player not available when trying to reconnect after guest login');
          }
        }, 500);
      } else {
        console.log('[useWebSocket] Not reconnecting websocket - session, player, or socket connection missing');
      }
    }
    
    // Update ref
    wasGuestRef.current = isGuest;
  }, [isGuest, isAuthenticated, connect, disconnect]);

  return {
    // State
    socket: socketRef.current,
    isConnected: gameStoreRef.current.isConnected,
    isConnecting: isConnectingRef.current || (!gameStoreRef.current.isConnected && socketRef.current !== null),
    error: gameStoreRef.current.error?.message || null,
    reconnectAttempts: gameStoreRef.current.reconnectAttempts,
    
    // Actions
    connect,
    disconnect,
    joinSession,
    leaveSession,
    sendPlayerAction,
    sendPlayerReady,
  };
};

export default useWebSocket;
